import { useEffect, useRef, useState } from "react";

// status: connecting | open | reconnecting
export function useReconnectingSocket({ url, enabled = true, onMessage, beforeConnect, onOpen }) {
  const [status, setStatus] = useState("connecting");
  const refs = useRef({});
  refs.current = { onMessage, beforeConnect, onOpen };

  useEffect(() => {
    if (!enabled || !url) return undefined;
    let ws;
    let timer;
    let ping;
    let attempts = 0;
    let stopped = false;

    const connect = async () => {
      if (stopped) return;
      setStatus(attempts ? "reconnecting" : "connecting");
      try {
        if (refs.current.beforeConnect) await refs.current.beforeConnect(attempts);
      } catch {
        /* recover on next attempt */
      }
      if (stopped) return;
      ws = new WebSocket(url);
      ws.onopen = () => {
        const wasReconnect = attempts > 0;
        attempts = 0;
        setStatus("open");
        refs.current.onOpen?.(wasReconnect);
        ping = setInterval(() => ws.readyState === 1 && ws.send("ping"), 25000);
      };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.event !== "pong") refs.current.onMessage?.(msg);
        } catch {
          /* ignore malformed */
        }
      };
      ws.onclose = () => {
        clearInterval(ping);
        if (stopped) return;
        attempts += 1;
        setStatus("reconnecting");
        timer = setTimeout(connect, Math.min(1000 * 2 ** Math.min(attempts, 4), 15000));
      };
      ws.onerror = () => ws.close();
    };

    const onOnline = () => {
      if (ws && ws.readyState !== 1) {
        clearTimeout(timer);
        connect();
      }
    };
    window.addEventListener("online", onOnline);
    connect();
    return () => {
      stopped = true;
      window.removeEventListener("online", onOnline);
      clearTimeout(timer);
      clearInterval(ping);
      if (ws) ws.close();
    };
  }, [url, enabled]);

  return status;
}

export function useNow(intervalMs = 1000) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}
